import { WorkspaceLocale } from "@/constants/workplaces";
import { type Component, markRaw, toRaw } from "vue";

const S3_FAVICON_URL = import.meta.env.VITE_S3_FAVICON_URL;

/**
 * Detects the browser's preferred locale and maps it to a supported {@link WorkspaceLocale}.
 *
 * Resolution order:
 * 1. Exact case-insensitive match against `navigator.language`
 * 2. Prefix match (e.g. `es-MX` → `WorkspaceLocale.ES`)
 * 3. Falls back to `WorkspaceLocale.EN_US` when no match is found
 *
 * @example
 * // Browser language: "en-US"
 * getBrowserLocale(); // → WorkspaceLocale.EN_US
 *
 * @example
 * // Browser language: "fr-CA" (exact match takes precedence over prefix)
 * getBrowserLocale(); // → WorkspaceLocale.FR_CA
 */
export const getBrowserLocale = (): WorkspaceLocale => {
  const browserLanguage = navigator.language || (navigator.languages ?? [])[0];
  if (!browserLanguage) return WorkspaceLocale.EN_US;
  const locales: WorkspaceLocale[] = Object.values(WorkspaceLocale);

  const normalizedBrowserLanguage = browserLanguage.toLowerCase();

  const exactMatch = locales.find((locale) => locale.toLowerCase() === normalizedBrowserLanguage);
  if (exactMatch) return exactMatch;

  const partialMatch = locales.find((locale) =>
    normalizedBrowserLanguage.startsWith(locale.toLowerCase().split("-")[0])
  );

  return partialMatch ?? WorkspaceLocale.EN_US;
};

/**
 * Applies a custom favicon and browser tab title for known white-label subdomains.
 * Has no effect for subdomains not in the allow-list.
 *
 * @param subdomain - The Coro subdomain extracted from the current URL.
 */
export const handleFaviconAndTitle = (subdomain: string) => {
  const subdomainsWithCustomFaviconAndTitle = ["domain-test", "keplersafe"];
  const domainsToDispayName: Record<string, string> = {
    keplersafe: "Kepler Safe",
    "domain-test": "Domain Test",
  };
  if (subdomainsWithCustomFaviconAndTitle.includes(subdomain)) {
    const favicon16 = document.getElementById("favicon-16") as HTMLLinkElement;
    const favicon32 = document.getElementById("favicon-32") as HTMLLinkElement;
    const faviconShortcut = document.getElementById("fav-shortcut") as HTMLLinkElement;
    const title = document.getElementById("browser-tab-title") as HTMLTitleElement;
    const favManifest = document.getElementById("fav-manifest") as HTMLLinkElement;
    const favBrowserConfig = document.getElementById("fav-browserconfig") as HTMLMetaElement;
    favicon16.href = `${S3_FAVICON_URL}/${subdomain}/favicon-16x16.png`;
    favicon32.href = `${S3_FAVICON_URL}/${subdomain}/favicon-32x32.png`;
    faviconShortcut.href = `${S3_FAVICON_URL}/${subdomain}/favicon.ico`;
    favManifest.href = `${S3_FAVICON_URL}/${subdomain}/site.webmanifest`;
    favBrowserConfig.content = `${S3_FAVICON_URL}/${subdomain}/browserconfig.xml`;
    title.innerText = `${domainsToDispayName[subdomain]} Console`;
  }
};

/**
 * Optimizes dynamic components by preventing unnecessary remounts.
 *
 * When a component definition is stored in a reactive state (ref/reactive)
 * and used in `<component :is="...">`, Vue wraps it in a Proxy.
 * Parent component updates can cause Vue to see a new Proxy,
 * triggering a costly remount and losing the component's state.
 *
 * Uses `toRaw` to unwrap the Proxy and `markRaw` to opt the result out of Vue's
 * reactivity system permanently — preserving component state and improving performance.
 *
 * @param comp - The Vue component, potentially wrapped in a reactive Proxy.
 * @returns The raw, non-reactive component definition, safe for use with `:is`.
 *
 * @example
 * const comp = computed(() => markComponentAsPlain(MyComponentA));
 * // <component :is="comp" />
 */
export const markComponentAsPlain = (comp: Component): Component => {
  return markRaw(toRaw(comp));
};
